import { getAnalytics } from "@tm/insurance/analytics";
import { trackComponentView, trackEvent } from "@tm/insurance/analytics/xdm";
import {
  assignSelectionOrdersToExcelTravellers,
  assignSelectionOrdersToPrefilledTravellers,
  downloadTemplate,
  ensureTravellerIds,
  getFormFieldErrorMsg,
  getNextNegativeTravellerId,
  getNextSelectionOrder,
  getTravDetailsFromMemory,
  setTravDetailsInMemory,
} from "@tm/insurance/utils";
import { GENDER_VALUES, TextNodeType } from "@tm/isomorphic-utils";
import { LandingButtonTertiary } from "@tm/static";
import { ErrorIcon } from "@tm/static/icons/ErrorIcon";
import { usePlanType } from "@tm/ui/contexts";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import {
  FieldArrayWithId,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import TravellerDetailsV2 from "../AddTravellerDetail/TravellerDetailsV2";
import { TravellerData } from "../ExcelUpload";
import { ExcelUploadErrorCode } from "../ExcelUpload/ExcelUpload.constants";
import FlexComp from "../FlexComp/FlexComp";
import FormattedText from "../FormattedText/FormattedText";
import { ImageComponent } from "../ImageComponent/ImageComponent";
import {
  AddButtonWrap,
  AddTravContainer,
  AddTravHeader,
  AddViewButton,
  ArrowIcon,
  ButtonWrap,
  ExcelErrorMessage,
  ExcelPersuasion,
  ExcelWrapper,
  Separator,
  ViewButtonWrap,
} from "./AddTravellerDetailsComp.styles";

const ExcelUpload = lazy(() => import("../ExcelUpload/ExcelUpload"));

export interface IGender {
  id: string;
  label: string;
  isSelected: boolean;
}

interface ISecondaryContactV2 {
  name: string;
  dob: string;
  gender: string;
  isSelected: boolean;
  isEdited?: boolean;
  isSavedTraveller?: boolean;
  isExcelUploaded?: boolean;
  passport?: string;
  nomineeName?: string;
  travellerId?: number | string;
  infoSource?: string;
  selectionOrder?: number;
}

interface TravellerError {
  index: number;
  errorType: string;
}

type FormFieldType = "name" | "dob" | "gender" | "passport" | "nomineeName";

export interface IPrimaryContact {
  email: string;
  mobile: string;
}

type ITravellerData = {
  travellerData: ISecondaryContactV2[];
};

export interface ITravellerDetailsFormV2 {
  travellerData: ISecondaryContactV2[];
  primaryContactData: IPrimaryContact;
  tncs: boolean[];
}

export interface IFormFieldType {
  fieldName?: string;
  isEnabled?: boolean;
  isRequired?: boolean;
  regex?: string;
  title?: string;
  value?: string;
  values?: IGender[];
  placeholder?: string;
  errorMessage?: string;
  countryCode?: string;
}

type AddTravellerDetailsCompProps = {
  title: string;
  subTitle: string;
  savedTravellers?: {
    name: string;
    gender?: string;
    dob?: number;
    passport?: string;
    nomineeName?: string;
    isDefaultSelected?: boolean;
    travellerId?: number | string;
    infoSource?: string;
  }[];
  secondaryContact: {
    title: string;
    isEditable?: boolean;
    fields: {
      name?: IFormFieldType;
      dob?: IFormFieldType;
      gender?: IFormFieldType;
      passport?: IFormFieldType;
      nomineeName?: IFormFieldType;
    };
  };
  addTravellerCTA: TextNodeType;
  isPassportMandatory: boolean;
  travellerCount?: number;
  formValues: any;
  setValue: any;
  firstErrorIndex?: number;
  setFirstErrorIndex?: (index: number) => void;
  isLoading?: boolean;
  onPayment?: () => void;
  primaryContactRef?: React.RefObject<HTMLDivElement>;
  tncRef?: React.RefObject<HTMLDivElement>;
  isDomesticBooking?: boolean;
  expandedTravellerIndex?: number;
  setExpandedTravellerIndex?: (index: number) => void;
  setSecondaryContactFields: (fields: any) => void;
  visibleTravellersCount: number;
  excelSectionData?: any;
  onExcelFileUploaded?: (file: File | null) => void;
  forceShowAllTravellers?: boolean;
};

const AddTravellerDetailsComp = (data: AddTravellerDetailsCompProps) => {
  const queryParams = new URLSearchParams(window.location.search);
  const quoteId = queryParams.get("quoteId") || "";
  const itineraryId = queryParams.get("itineraryId") || "";
  const [localAddTrav, setLocalAddTrav] = useState(-1);
  const travellerIndex = 0;
  const {
    formValues,
    title,
    subTitle,
    savedTravellers,
    secondaryContact,
    addTravellerCTA,
    isPassportMandatory,
    travellerCount = 1,
    setValue,
    firstErrorIndex,
    setFirstErrorIndex,
    isLoading = false,
    onPayment,
    primaryContactRef,
    tncRef,
    isDomesticBooking = false,
    expandedTravellerIndex,
    setExpandedTravellerIndex,
    setSecondaryContactFields,
    visibleTravellersCount,
    excelSectionData,
    onExcelFileUploaded,
    forceShowAllTravellers,
  } = data;

  const scrollToTravellerWithOffset = (index: number) => {
    const element = travellerRefMap.current?.[index];
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const offsetTop = elementRect.top + window.pageYOffset - 100;

      window.scrollTo({
        top: offsetTop,
        behavior: "smooth",
      });
    }
  };
  // Use external expanded state if provided, otherwise use local state
  const addTrav =
    expandedTravellerIndex !== undefined
      ? expandedTravellerIndex
      : localAddTrav;
  const setAddTrav = setExpandedTravellerIndex || setLocalAddTrav;

  // Sync local state with external state when external state changes
  useEffect(() => {
    if (
      expandedTravellerIndex !== undefined &&
      expandedTravellerIndex !== localAddTrav
    ) {
      setLocalAddTrav(expandedTravellerIndex);
    }
  }, [expandedTravellerIndex, localAddTrav]);

  const analytics = getAnalytics();

  const [showAllTravellers, setShowAllTravellers] = useState<boolean>(false);

  // Show all travellers when parent forces it (e.g., when error is on non-visible traveller)
  useEffect(() => {
    if (forceShowAllTravellers) {
      setShowAllTravellers(true);
    }
  }, [forceShowAllTravellers]);

  const [isSelectedError, setIsSelectedError] = useState<boolean>(false);
  const [isFieldError, setIsFieldError] = useState<boolean>(false);
  const [excelUploadError, setExcelUploadError] = useState<string>("");
  const [excelUploadInfo, setExcelUploadInfo] = useState<string>("");
  const [downloadTemplateError, setDownloadTemplateError] =
    useState<string>("");
  const [excelTotalCount, setExcelTotalCount] = useState<number>(0);

  const [travellerDataFromStore, setTravellerDataFromStore] = useState<any>({});
  const { travellerDetails } = travellerDataFromStore || {};

  // Read session storage only after client-side hydration
  useEffect(() => {
    const dataFromStorage = getTravDetailsFromMemory();
    setTravellerDataFromStore(dataFromStorage);
  }, []);

  useEffect(() => {
    if (excelSectionData) {
      analytics?.trackTravDetailsEvents({
        actionType: "download_template_shown",
      });
      analytics?.trackTravDetailsEvents({
        actionType: "upload_template_shown",
      });
      trackComponentView({
        name: "download_template_shown",
        type: "view",
      });
      trackComponentView({
        name: "upload_template_shown",
        type: "view",
      });
    }
  }, [excelSectionData]);

  // Handle firstErrorIndex - scroll to error traveller when index changes
  useEffect(() => {
    if (firstErrorIndex && firstErrorIndex > -1) {
      travellerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      setAddTrav(-1);
    }
    if (setFirstErrorIndex) {
      setFirstErrorIndex(-1);
    }
  }, [firstErrorIndex, setFirstErrorIndex]);

  useEffect(() => {
    let travellerDetail: any = [];
    const queryParams = new URLSearchParams(window.location.search);
    const partnerBookingId = queryParams.get("partnerBookingId");
    const shouldUseApiSavedTravellers =
      !travellerDetails?.length ||
      queryParams.get("isUpcomingTrip") === "true" ||
      Boolean(partnerBookingId);

    setSecondaryContactFields(secondaryContact?.fields);
    if (shouldUseApiSavedTravellers) {
      savedTravellers?.map((data: any) => {
        travellerDetail.push({
          ...data,
          gender:
            data.gender.charAt(0).toUpperCase() +
            data.gender.slice(1).toLowerCase(),
          isSelected: data?.isDefaultSelected || false,
          isSavedTraveller: true,
          isEdited: false,
          travellerId: data.travellerId,
          infoSource: data.infoSource,
        });
      });
      setTravDetailsInMemory({
        ...travellerDataFromStore,
        travellerDetails: travellerDetail,
      });
      // Only expand first traveller if no excelSectionData (since we won't have default empty traveller)
      if (travellerDetail.length === 0 && !excelSectionData) {
        setAddTrav(0);
      }
    } else if (travellerDetails && travellerDetails.length > 0) {
      // Use existing session storage data when available
      // This preserves user's exact form data including names, DOB, etc.
      travellerDetail = [...travellerDetails];
    } else {
      // Fallback to create new traveller entries if session storage is empty/undefined
      const queryParams = new URLSearchParams(window.location.search);
      const travellers: number = Number(queryParams.get("travellers"));
      const travellercount = travellerCount || travellers || 1;
      travellerDetail = Array.from({ length: travellercount }).map(() => ({
        name: "",
        dob: "",
        gender: "",
        isSelected: true,
        isEdited: true,
        isSavedTraveller: false,
        passport: "",
        nomineeName: "",
      }));
    }
    // If excelSectionData is present, don't create default empty traveller
    let length = travellerDetail?.length
      ? travellerDetail?.length
      : excelSectionData
      ? 0
      : 1;

    const rawFormData = Array.from({
      length: length,
    }).map((_, index) => ({
      name: travellerDetail?.[index]?.name || "",
      dob: travellerDetail?.[index]?.isSavedTraveller
        ? travellerDetail?.[index]?.dob || ""
        : travellerDetail?.[index]?.dob || "yyyy-mm-dd",
      gender: travellerDetail?.[index]?.gender || "",
      passport: travellerDetail?.[index]?.passport || "",
      nomineeName: travellerDetail?.[index]?.nomineeName || "",
      isSelected: travellerDetail?.[index]?.isSelected ?? true,
      isEdited: travellerDetail?.[index]?.isEdited ?? true,
      isSavedTraveller: travellerDetail?.[index]?.isSavedTraveller ?? false,
      travellerId: travellerDetail?.[index]?.travellerId,
      infoSource: travellerDetail?.[index]?.infoSource,
      selectionOrder: travellerDetail?.[index]?.selectionOrder,
    }));
    const { travellers: formDataWithOrder } =
      assignSelectionOrdersToPrefilledTravellers(rawFormData);
    setValue("travellerData", ensureTravellerIds(formDataWithOrder));
  }, [secondaryContact, savedTravellers, travellerDataFromStore]);

  const {
    control,
    formState: { errors },
    getValues,
    trigger,
    getFieldState,
    setError,
    clearErrors,
    handleSubmit,
  } = useFormContext<ITravellerDetailsFormV2>();
  const { type } = usePlanType();

  const {
    fields: travellers,
    prepend,
    replace,
  } = useFieldArray({
    control,
    name: "travellerData",
  });

  const handleErrorToast = () => {
    setTimeout(() => {
      setIsSelectedError(false);
      setIsFieldError(false);
    }, 3000);
  };

  const handleGenderChange = (data: IGender, index: number) => {
    setValue(`travellerData.${index}.gender`, data.label);
  };

  const saveFormValuesOnBlur = (index: number = -1) => {
    if (index != -1 && !getValues(`travellerData.${index}.isEdited`)) {
      setValue(`travellerData.${index}.isEdited`, true);
    }
    const travellerData = getValues("travellerData") || [];
    const { primaryContactData } = formValues;
    setTravDetailsInMemory({
      ...travellerDataFromStore,
      travellerDetails: travellerData,
      primaryContactDetails: primaryContactData,
    });
  };

  const handleArrowButton = (index: number) => {
    if (addTrav === index) {
      setAddTrav(-1);
      if (travellerData?.[index].isSelected) {
        trigger(`travellerData.${index}`);
      }
    } else {
      setAddTrav(index);
    }
  };

  const error = errors?.travellerData?.[travellerIndex];
  const travellerRefMap = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const travellerData = useWatch({
    control,
    name: "travellerData",
  });

  const selectedCount = useMemo(
    () =>
      travellerData?.filter((traveller) => traveller.isSelected)?.length ?? 0,
    [travellerData]
  );

  // Check if any selected traveller has missing mandatory field data
  const hasMissingMandatoryData = useMemo(() => {
    if (!travellerData) return false;

    const { name, dob, gender, passport, nomineeName } =
      secondaryContact?.fields || {};

    return travellerData.some((traveller) => {
      if (!traveller.isSelected) return false;

      // Check each mandatory field
      if (name?.isRequired && !traveller.name?.trim()) return true;
      if (dob?.isRequired && (!traveller.dob || traveller.dob === "yyyy-mm-dd"))
        return true;
      if (gender?.isRequired && !traveller.gender?.trim()) return true;
      if (passport?.isRequired && !traveller.passport?.trim()) return true;
      if (nomineeName?.isRequired && !traveller.nomineeName?.trim())
        return true;

      return false;
    });
  }, [travellerData, secondaryContact?.fields]);

  // Helper to replace all placeholders consistently
  // Placeholder convention: {X} = uploaded/selected, {Y} = remaining, {Z} = total, {W} = duplicates
  const replaceAllPlaceholders = (
    message: string,
    x: number,
    y: number,
    z: number,
    w: number = 0
  ) =>
    message
      ?.replace("{X}", x?.toString() || "0")
      ?.replace("{Y}", y?.toString() || "0")
      ?.replace("{Z}", z?.toString() || "0")
      ?.replace("{W}", w?.toString() || "0");

  // Compute the message to display in ExcelPersuasion
  const excelPersuasionMessage = useMemo(() => {
    const isExcelBypass = excelSectionData?.upload?.isExcelBypass;
    const remaining = travellerCount - selectedCount;

    if (isExcelBypass) {
      return excelSectionData?.bypassInfo;
    }

    // Highest priority: Excel had more travellers than needed (only first X were added)
    if (selectedCount === travellerCount && excelTotalCount > travellerCount) {
      return replaceAllPlaceholders(
        excelSectionData?.info?.traveller_added_first,
        selectedCount,
        remaining,
        travellerCount
      );
    }

    if (selectedCount === travellerCount) {
      if (hasMissingMandatoryData) {
        return replaceAllPlaceholders(
          excelSectionData?.info?.details_missing,
          selectedCount,
          0,
          travellerCount
        );
      }
      return replaceAllPlaceholders(
        excelSectionData?.info?.traveller_added,
        selectedCount,
        0,
        travellerCount
      );
    }

    if (excelUploadInfo && !excelUploadError) {
      return excelUploadInfo;
    }

    return replaceAllPlaceholders(
      excelSectionData?.info?.traveller_input,
      selectedCount,
      remaining,
      travellerCount
    );
  }, [
    excelSectionData,
    selectedCount,
    travellerCount,
    hasMissingMandatoryData,
    excelUploadInfo,
    excelUploadError,
    excelTotalCount,
  ]);

  const getVisibleTravellers = () => {
    return showAllTravellers
      ? travellers
      : travellers.slice(0, visibleTravellersCount);
  };
  const travellerRef = useRef<HTMLInputElement>(null);

  const filterTravellers = async (
    selected: number[],
    errors: TravellerError[]
  ) => {
    let targetPosition = 0;
    const newIndices: any = new Array(travellers.length);
    const errorIndices = errors.map((err) => err.index);

    travellers.forEach((traveller, index) => {
      if (errorIndices.includes(index)) {
        newIndices[index] = targetPosition;
        targetPosition++;
      }
    });

    travellers.forEach((traveller, index) => {
      if (selected.includes(index) && !errorIndices.includes(index)) {
        newIndices[index] = targetPosition;
        targetPosition++;
      }
    });

    travellers.forEach((traveller, index) => {
      if (!selected.includes(index) && !errorIndices.includes(index)) {
        newIndices[index] = targetPosition;
        targetPosition++;
      }
    });

    const newArray: any = [];
    newIndices.forEach((indexVal: number, index: number) => {
      newArray[indexVal] = getValues(`travellerData.${index}`);
    });
    replace(newArray);
    const isValid = await trigger("travellerData");

    // If there are errors, show all travellers to make error travellers visible
    if (errors.length > 0) {
      setShowAllTravellers(true);
    }

    errors.forEach(({ index, errorType }, ind) => {
      const fieldKey = `travellerData.${ind}.${errorType}` as
        | `travellerData.${number}.name`
        | `travellerData.${number}.dob`
        | `travellerData.${number}.gender`
        | `travellerData.${number}.passport`
        | `travellerData.${number}.nomineeName`;

      setError(fieldKey, {
        message: getFormFieldErrorMsg(errorType as FormFieldType),
      });
    });
  };

  const handleTravellerSelect = (value: boolean, index: number) => {
    if (getValues(`travellerData.${index}.isSelected`)) {
      setAddTrav(-1);
      setValue(`travellerData.${index}.isSelected`, false);
      // Clear excel upload info so message falls back to dynamic count
      setExcelUploadInfo("");
      setExcelTotalCount(0);
    } else {
      if (selectedCount === travellerCount) {
        if (!isSelectedError) {
          setIsSelectedError(true);
          handleErrorToast();
        }
      } else {
        setAddTrav(index);
        setValue(`travellerData.${index}.isSelected`, true);
        setValue(
          `travellerData.${index}.selectionOrder`,
          getNextSelectionOrder(getValues("travellerData") || [])
        );
        // Clear excel upload info so message falls back to dynamic count
        setExcelUploadInfo("");
        setExcelTotalCount(0);
      }
    }
  };

  const handleExcelDataParsed = (
    data: TravellerData[],
    duplicatesRemoved: number = 0,
    file?: File
  ) => {
    analytics?.trackTravDetailsEvents({
      actionType: "upload_template_clicked",
    });
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "upload_template_clicked",
      },
    });

    // Notify parent about the uploaded file
    if (file) {
      onExcelFileUploaded?.(file);
    }

    // Clear any previous error/info
    setExcelUploadError("");
    setExcelUploadInfo("");

    // Clear all previous traveller validation errors
    clearErrors("travellerData");

    // Get info messages config
    const infoMessages = excelSectionData?.info;

    // Get current traveller data
    const currentTravellerData = getValues("travellerData") || [];

    // Remove previously uploaded Excel travellers and deselect the remaining ones
    const deselectedTravellers = currentTravellerData
      .filter((traveller: any) => !traveller.isExcelUploaded)
      .map((traveller: any) => ({
        ...traveller,
        isSelected: false,
      }));

    // Pick only top (travellerCount) travellers from Excel
    const travellersToAdd = data.slice(0, travellerCount);
    const totalFromExcel = data.length;
    const addedCount = travellersToAdd.length;

    // Store total from Excel for use in dynamic messages
    setExcelTotalCount(totalFromExcel);

    // Map Excel data to form traveller format
    const newTravellers = ensureTravellerIds(
      assignSelectionOrdersToExcelTravellers(
        travellersToAdd.map((traveller) => ({
          name: traveller.name || "",
          dob: traveller.dob || "yyyy-mm-dd",
          gender: traveller.gender
            ? traveller.gender.charAt(0).toUpperCase() +
              traveller.gender.slice(1).toLowerCase()
            : "",
          passport: traveller.passport || "",
          nomineeName: traveller.nomineeName || "",
          isSelected: true,
          isEdited: true,
          isSavedTraveller: false,
          isExcelUploaded: true,
        }))
      )
    );

    // Replace all travellers: new Excel travellers (selected) at top + existing deselected travellers
    const updatedTravellerDetails = [...newTravellers, ...deselectedTravellers];
    replace(updatedTravellerDetails);

    // Save to session storage
    setTravDetailsInMemory({
      ...travellerDataFromStore,
      travellerDetails: updatedTravellerDetails,
    });

    // Collapse all traveller cards
    setAddTrav(-1);

    // Set appropriate info message
    const stillNeeded = travellerCount - addedCount;

    if (addedCount < totalFromExcel) {
      // Only first X travellers were added (due to limit) - highest priority
      setExcelUploadInfo(
        replaceAllPlaceholders(
          infoMessages.traveller_added_first,
          addedCount,
          stillNeeded,
          travellerCount,
          duplicatesRemoved
        )
      );
    } else if (duplicatesRemoved > 0) {
      // Duplicates were removed
      setExcelUploadInfo(
        replaceAllPlaceholders(
          infoMessages.traveller_added_duplicate,
          addedCount,
          stillNeeded,
          travellerCount,
          duplicatesRemoved
        )
      );
    } else if (addedCount === travellerCount) {
      // All required travellers added successfully
      setExcelUploadInfo(
        replaceAllPlaceholders(
          infoMessages.traveller_added,
          addedCount,
          stillNeeded,
          travellerCount,
          duplicatesRemoved
        )
      );
    } else if (stillNeeded > 0) {
      // Partial add - need more travellers
      setExcelUploadInfo(
        replaceAllPlaceholders(
          infoMessages.traveller_added_partial,
          addedCount,
          stillNeeded,
          travellerCount,
          duplicatesRemoved
        )
      );
    } else {
      // All required travellers added
      setExcelUploadInfo(
        replaceAllPlaceholders(
          infoMessages.traveller_added,
          addedCount,
          stillNeeded,
          travellerCount,
          duplicatesRemoved
        )
      );
    }
  };

  const handleExcelError = (
    errorCode: ExcelUploadErrorCode,
    missingFields?: string[]
  ) => {
    // Clear any info message and download template error when there's an upload error
    setExcelUploadInfo("");
    setDownloadTemplateError("");

    const errorMessages = excelSectionData?.errors;

    // Map error codes to display messages
    const errorCodeToMessage: Record<ExcelUploadErrorCode, string> = {
      [ExcelUploadErrorCode.FILE_SIZE_EXCEEDED]:
        errorMessages.file_size_exceeds,
      [ExcelUploadErrorCode.UNSUPPORTED_FILE_TYPE]:
        errorMessages.unsupported_file_type,
      [ExcelUploadErrorCode.UNABLE_TO_READ_FILE]:
        errorMessages.unable_to_read_file,
      [ExcelUploadErrorCode.NO_DETAILS_FOUND]: errorMessages.no_details_found,
      [ExcelUploadErrorCode.TOO_MANY_ROWS]: errorMessages.no_details_found,
      [ExcelUploadErrorCode.FILE_FORMAT_ERROR]: errorMessages.file_format,
      [ExcelUploadErrorCode.FIELDS_MISSING]: errorMessages.fields_missing,
      [ExcelUploadErrorCode.UNKNOWN_ERROR]: errorMessages.file_format,
    };

    let mappedError =
      errorCodeToMessage[errorCode] || errorMessages.file_format;

    // Handle missing fields with capitalized field names
    if (errorCode === ExcelUploadErrorCode.FIELDS_MISSING && missingFields) {
      const fieldNameMap: Record<string, string> = {
        name: "Name",
        dob: "DOB",
        gender: "Gender",
        passport: "Passport",
        nomineename: "Nominee Name",
      };
      const capitalizedFields = missingFields
        .map(
          (f) =>
            fieldNameMap[f.toLowerCase()] ||
            f.charAt(0).toUpperCase() + f.slice(1)
        )
        .join(", ");
      mappedError = mappedError.replace("{fields}", capitalizedFields);
    }

    // Track excel error analytics
    analytics?.trackTravDetailsEvents({
      actionType: "error_shown",
      payload: `excel | ${mappedError}`,
    });

    setExcelUploadError(mappedError);
  };

  const handleDownloadTemplate = async () => {
    analytics?.trackTravDetailsEvents({
      actionType: "download_template_clicked",
    });
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "download_template_clicked",
      },
    });

    // Clear previous errors
    setDownloadTemplateError("");
    setExcelUploadError("");

    try {
      await downloadTemplate(itineraryId);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to download template";
      setDownloadTemplateError(errorMessage);
      // Clear upload error when download error occurs
      setExcelUploadError("");
      console.error("Failed to download template:", error);
    }
  };

  const handleAddTraveller = () => {
    analytics?.trackTravDetailsEvents({
      actionType: "Add_another_traveller_clicked",
    });
    if (selectedCount != travellerCount) {
      setAddTrav(0);
      const currentTravellers = getValues("travellerData") || [];
      prepend({
        name: "",
        dob: "yyyy-mm-dd",
        gender: "",
        passport: "",
        nomineeName: "",
        isSelected: true,
        selectionOrder: getNextSelectionOrder(currentTravellers),
        travellerId: getNextNegativeTravellerId(currentTravellers),
      });
      setTimeout(() => {
        scrollToTravellerWithOffset(0);
      }, 100);
    }
  };

  const handleViewTraveller = () => {
    analytics?.trackTravDetailsEvents({
      actionType: showAllTravellers
        ? "viewLessButton_clicked"
        : "viewAllButton_clicked",
    });
    setShowAllTravellers(!showAllTravellers);
  };

  return (
    <AddTravContainer>
      <AddTravHeader>
        <FlexComp flexDirection="column" gap="8px">
          <FormattedText
            value={title}
            baseStyle={{
              fontSize: "22px",
              fontWeight: "900",
              color: "#000000",
            }}
          />
          <FormattedText
            value={subTitle}
            baseStyle={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#4a4a4a",
            }}
          />
        </FlexComp>
        {travellerCount &&
        travellerCount > 0 &&
        !excelSectionData?.upload?.isExcelBypass ? (
          <FormattedText
            value={
              selectedCount?.toString() +
              "/" +
              travellerCount?.toString() +
              " Added"
            }
            baseStyle={{
              color: "#757575",
              fontSize: "14px",
              fontWeight: "700",
            }}
          />
        ) : null}
      </AddTravHeader>
      {excelSectionData && (
        <>
          <Separator />
          <ExcelWrapper>
            {excelSectionData.description && (
              <FormattedText
                baseStyle={{
                  fontSize: "14px",
                  fontWeight: "400",
                  color: "#4a4a4a",
                }}
                value={excelSectionData.description}
              />
            )}
            <ButtonWrap>
              <LandingButtonTertiary
                title={
                  excelSectionData.download?.btnText || "Download Template"
                }
                onClick={handleDownloadTemplate}
                disableState={false}
              />
              <Suspense fallback={null}>
                <ExcelUpload
                  onDataParsed={handleExcelDataParsed}
                  onError={handleExcelError}
                  isExcelBypass={excelSectionData.upload?.isExcelBypass}
                  maxFileSizeMB={excelSectionData.upload?.maxFileSizeMB}
                  maxRows={excelSectionData.upload?.maxRows}
                  buttonText={excelSectionData.upload?.buttonText}
                  requiredFields={excelSectionData.upload?.requiredFields}
                  errorMessages={excelSectionData.errors}
                  hideError
                  quoteId={quoteId}
                  itineraryId={itineraryId}
                />
              </Suspense>
            </ButtonWrap>
            {(excelUploadError || downloadTemplateError) && (
              <ExcelErrorMessage>
                <ErrorIcon />
                <FormattedText
                  value={excelUploadError || downloadTemplateError}
                />
              </ExcelErrorMessage>
            )}
            <ExcelPersuasion>
              <FormattedText value={excelPersuasionMessage} />
            </ExcelPersuasion>
          </ExcelWrapper>
        </>
      )}
      {getVisibleTravellers().map(
        (
          traveller: FieldArrayWithId<
            ITravellerDetailsFormV2,
            "travellerData",
            "id"
          >,
          index: number
        ) => {
          const { gender, isSelected } = traveller || {};
          return (
            <div
              key={traveller.id}
              ref={(el) => {
                travellerRefMap.current[index] = el;
              }}
            >
              <TravellerDetailsV2
                type={type}
                handleTravellerSelect={handleTravellerSelect}
                travellerIndex={index}
                travellerRef={travellerRef}
                isLoading={isLoading}
                firstErrorIndex={firstErrorIndex ?? -1}
                travellerSelected={addTrav == index}
                isSelected={isSelected}
                count={selectedCount}
                travellerNumber={`Traveller ${index + 1}`}
                isSavedTravellers={savedTravellers?.length ? true : false}
                secondaryContactData={secondaryContact?.fields}
                genderTypes={
                  secondaryContact?.fields?.gender?.values || GENDER_VALUES
                }
                selectedGender={gender}
                handleGenderChange={(item) => handleGenderChange(item, index)}
                handleArrowButton={() => handleArrowButton(index)}
                saveFormValuesOnBlur={saveFormValuesOnBlur}
                //   isDomesticBooking={isDomesticBooking}
                // isValidForm={isValidForm}
                isEditable={
                  secondaryContact?.isEditable === false ? false : true
                } //
              />
            </div>
          );
        }
      )}
      {!excelSectionData?.upload?.isExcelBypass && (
        <>
          <Separator />
          <AddViewButton>
            <AddButtonWrap
              isDisabled={selectedCount === travellerCount}
              onClick={handleAddTraveller}
            >
              <ImageComponent
                width="16px"
                height="16px"
                margin="0px 10px 0px 0px"
                src={
                  selectedCount === travellerCount
                    ? "https://tripmoneycmsimgak.mmtcdn.com/img/Vector_529ac663fb.svg"
                    : "https://tripmoneycmsimgak.mmtcdn.com/img/Vector_68e1489f3a.svg"
                }
              />

              <FormattedText
                value={"ADD ANOTHER TRAVELLER"}
                baseStyle={{
                  fontWeight: "800",
                }}
              />
            </AddButtonWrap>

            {travellers.length > visibleTravellersCount && (
              <ViewButtonWrap onClick={handleViewTraveller}>
                <FormattedText
                  value={showAllTravellers ? "VIEW LESS" : "VIEW ALL"}
                  baseStyle={{
                    fontWeight: "800",
                  }}
                />

                <ArrowIcon
                  isRotated={showAllTravellers}
                  src="https://tripmoneycmsimgak.mmtcdn.com/img/Arrow_Down_Blue_5d58cf6783.svg"
                  alt="View toggle icon"
                />
              </ViewButtonWrap>
            )}
          </AddViewButton>
        </>
      )}
    </AddTravContainer>
  );
};

export default AddTravellerDetailsComp;
